import { useState, useCallback, useEffect, memo } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useDeleteDataSourceMutation, useTestDataSourceMutation } from '@/hooks/useDataSources'
import { useConnectionStore } from '@/stores/connectionStore'
import { useNavigate } from 'react-router'
import type { DataSource } from '@/services/api'
import {
  ServerIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  LockClosedIcon,
  Bars2Icon,
  ServerStackIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { notify } from '@/utils/notify'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'
import { SafetyLockDialog } from './SafetyLockDialog'
import { getAgentEncryptionSettings } from '@/utils/agentEncryption'
import { useAgents } from '@/hooks/useAgents'

interface ConnectionRowProps {
  connection: DataSource
  onEdit: () => void
  isHighlighted?: boolean
  disabled?: boolean
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

// Helper to detect if a Redis connection is in cluster mode
const isRedisClusterConnection = (connection: DataSource): boolean => {
  if (connection.databaseType !== 'Redis') return false
  const params = connection.connectionParams as Record<string, unknown> | undefined
  return Array.isArray(params?.clusterNodes)
}

// Helper to get cluster node count
const getClusterNodeCount = (connection: DataSource): number => {
  const params = connection.connectionParams as Record<string, unknown> | undefined
  const nodes = params?.clusterNodes as unknown[] | undefined
  // Add 1 for the primary node (host:port)
  return Array.isArray(nodes) ? nodes.length + 1 : 0
}

export const ConnectionRow = memo(function ConnectionRow({ connection, onEdit, isHighlighted = false, disabled = false }: ConnectionRowProps) {
  const deleteConnectionMutation = useDeleteDataSourceMutation()
  const testConnectionMutation = useTestDataSourceMutation()
  const { setActiveConnection, activeConnection } = useConnectionStore()
  const connectionHealth = useConnectionStore(s => s.connectionHealth)
  const navigate = useNavigate()
  const [lastTestResult, setLastTestResult] = useState<boolean | null>(
    connection.testStatus === 'success' ? true : connection.testStatus === 'failed' ? false : null
  )
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSafetyLock, setShowSafetyLock] = useState(false)
  const { getAgent } = useAgents()
  const [isCheckingAgent, setIsCheckingAgent] = useState(false)

  const isActive = activeConnection?.id === connection.id
  const activeHealth = isActive ? connectionHealth : null
  // Badge only shows "Active" when the connection is both selected AND the
  // health monitor confirms the agent is reachable. Any other health state
  // while active is a degraded state.
  const badgeState: 'active' | 'offline' | 'error' | 'failed' | 'none' =
    isActive && activeHealth === 'healthy' ? 'active'
    : isActive && activeHealth === 'checking' ? 'active'
    : isActive && activeHealth === 'agent_offline' ? 'offline'
    : isActive && activeHealth === 'error' ? 'error'
    : lastTestResult === false ? 'failed'
    : 'none'

  // Reset test result when this connection is no longer active
  useEffect(() => {
    if (!isActive && lastTestResult !== null) {
      setLastTestResult(null)
    }
  }, [isActive, lastTestResult])

  // @dnd-kit sortable setup for smooth reordering animations
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `conn::${connection.id}`,
    disabled,
    data: {
      type: 'connection' as const,
      connectionId: connection.id,
      folderId: connection.folderId || null,
      connection,
    },
  })

  const sortableStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    zIndex: isDragging ? 50 : undefined,
    position: 'relative' as const,
  }

  // Validate if agent datasource has required encryption key
  const validateAgentEncryption = useCallback((datasource: DataSource): string | null => {
    if (!datasource.agentId) {
      return null
    }

    const encryptionSettings = getAgentEncryptionSettings(datasource.agentId)

    if (!encryptionSettings.key || encryptionSettings.key.trim().length === 0) {
      return `Agent datasource requires an encryption key to connect. Please configure the encryption key for this agent connection in the Agents page.`
    }

    return null
  }, [])

  // Check if agent is online before connecting
  const checkAgentOnline = useCallback(async (): Promise<boolean> => {
    if (!connection.agentId) {
      // If no agent, allow connection
      return true
    }

    setIsCheckingAgent(true)
    try {
      const agent = await getAgent(connection.agentId)
      if (!agent) {
        notify.error('Could not verify agent status. Please try again.')
        return false
      }

      if (agent.connection_status !== 'connected') {
        notify.error('The agent for this datasource is not online. Please make the agent online first before connecting.')
        return false
      }

      return true
    } catch {
      notify.error('Failed to check agent status. Please try again.')
      return false
    } finally {
      setIsCheckingAgent(false)
    }
  }, [connection.agentId, getAgent])

  // Actual connection logic without lock check
  const performConnection = useCallback(async () => {
    if (!connection.id) return

    // Check if agent is online first
    const agentOnline = await checkAgentOnline()
    if (!agentOnline) {
      setLastTestResult(false)
      return
    }

    const encryptionError = validateAgentEncryption(connection)
    if (encryptionError) {
      setLastTestResult(false)
      notify.error(encryptionError)
      return
    }

    try {
      const result = await testConnectionMutation.mutateAsync({
        datasourceId: connection.id,
        agentId: connection.agentId,
      })
      const status =
        isRecord(result) && typeof (result as { status?: unknown }).status === 'string'
          ? (result as { status?: string }).status
          : undefined
      const message =
        isRecord(result) && typeof (result as { message?: unknown }).message === 'string'
          ? (result as { message?: string }).message
          : undefined
      const success = status === 'success'

      setLastTestResult(success)

      if (success) {
        let mappedType: 'mysql' | 'postgresql' | 'redis' | 'memcached' | 'oracle' | 'mongodb' | 'clickhouse' | 'scylladb' | 'milvus' = 'postgresql'

        const dbType = connection.databaseType.toLowerCase()
        if (dbType === 'mysql') {
          mappedType = 'mysql'
        } else if (dbType === 'redis') {
          mappedType = 'redis'
        } else if (dbType === 'memcached') {
          mappedType = 'memcached'
        } else if (dbType === 'oracledb' || dbType === 'oracle') {
          mappedType = 'oracle'
        } else if (dbType === 'mongodb') {
          mappedType = 'mongodb'
        } else if (dbType === 'clickhouse') {
          mappedType = 'clickhouse'
        } else if (dbType === 'scylladb') {
          mappedType = 'scylladb'
        } else if (dbType === 'milvus') {
          mappedType = 'milvus'
        } else if (dbType === 'postgresql' || dbType === 'postgres') {
          mappedType = 'postgresql'
        }

        const connectionData = {
          id: connection.id,
          name: connection.displayName || `${connection.host}:${connection.port}`,
          type: mappedType,
          host: connection.host || '',
          port:
            connection.port ||
            (mappedType === 'mysql'
              ? 3306
              : mappedType === 'redis'
                ? 6379
                : mappedType === 'memcached'
                  ? 11211
                  : mappedType === 'oracle'
                    ? 1521
                    : mappedType === 'mongodb'
                      ? 27017
                      : mappedType === 'clickhouse'
                        ? 8123
                        : mappedType === 'scylladb'
                          ? 9042
                          : mappedType === 'milvus'
                            ? 19530
                            : 5432),
          username: connection.username || '',
          password: '',
          database: connection.defaultDatabase || undefined,
          agentId: connection.agentId,
          createdAt: connection.createdAt || new Date().toISOString(),
          updatedAt: connection.updatedAt || new Date().toISOString(),
        }

        setActiveConnection(connectionData)
        const targetUrl = `/explorer/${connection.id}`
        navigate(targetUrl)
      } else {
        notify.error(`Failed to connect to ${connection.displayName}: ${message || 'Unknown error'}`)
      }
    } catch {
      setLastTestResult(false)
      notify.error('Connection test failed')
    }
  }, [connection, checkAgentOnline, validateAgentEncryption, testConnectionMutation, setActiveConnection, navigate])

  const handleConnect = useCallback(async () => {
    if (!connection.id) return

    if (connection.isLocked) {
      setShowSafetyLock(true)
      return
    }

    await performConnection()
  }, [connection.id, connection.isLocked, performConnection])

  const handleSafetyLockConfirm = useCallback(() => {
    setShowSafetyLock(false)
    void performConnection()
  }, [performConnection])

  const handleSafetyLockClose = useCallback(() => {
    setShowSafetyLock(false)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!connection.id) return

    try {
      await deleteConnectionMutation.mutateAsync(connection.id)
      notify.success('Connection deleted')
    } catch {
      notify.error('Failed to delete connection')
    }
  }, [connection.id, deleteConnectionMutation])

  const handleConnectClick = useCallback(() => {
    void handleConnect()
  }, [handleConnect])

  const handleCloseDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    void handleDeleteConfirm()
  }, [handleDeleteConfirm])

  const getStatusColor = () => {
    if (badgeState === 'active') return 'text-green-600'
    if (badgeState === 'offline') return 'text-amber-600 dark:text-amber-400'
    if (badgeState === 'error') return 'text-red-600'
    if (lastTestResult === true) return 'text-green-600'
    if (badgeState === 'failed') return 'text-red-600'
    return 'text-gray-400'
  }

  const getStatusIcon = () => {
    if (testConnectionMutation.isPending) {
      return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
    }
    if (badgeState === 'active') return <CheckCircleIcon className="w-4 h-4" />
    if (badgeState === 'offline') return <ExclamationTriangleIcon className="w-4 h-4" />
    if (badgeState === 'error') return <XCircleIcon className="w-4 h-4" />
    if (lastTestResult === true) return <CheckCircleIcon className="w-4 h-4" />
    if (badgeState === 'failed') return <XCircleIcon className="w-4 h-4" />
    return <div className="w-2 h-2 rounded-full bg-current" />
  }

  const getStatusLabel = () => {
    switch (badgeState) {
      case 'active': return 'Active'
      case 'offline': return 'Agent Offline'
      case 'error': return 'Connection Error'
      case 'failed': return 'Failed'
      case 'none': return ''
    }
  }

  const getDatabaseTypeIcon = () => {
    switch (connection.databaseType) {
      case 'MySQL':
        return <img src="/images/logos/mysql-logo.avif" alt="MySQL" className="w-5 h-5" />
      case 'PostgreSQL':
        return <img src="/images/logos/postgresql-logo.avif" alt="PostgreSQL" className="w-5 h-5" />
      case 'OracleDB':
        return <img src="/images/logos/oracle-logo.png" alt="Oracle" className="w-5 h-5" />
      case 'Redis':
        // Note: Redis Cluster also uses 'Redis' databaseType with clusterNodes in connectionParams
        return <img src="/images/logos/redis-logo.svg" alt="Redis" className="w-5 h-5" />
      case 'Memcached':
        return <img src="/images/logos/memcached-logo.svg" alt="Memcached" className="w-5 h-5" />
      case 'MongoDB':
        return <img src="/images/logos/mongodb-logo.svg" alt="MongoDB" className="w-5 h-5" />
      case 'ClickHouse':
        return <img src="/images/logos/clickhouse-logo.svg" alt="ClickHouse" className="w-5 h-5" />
      case 'ScyllaDB':
        return <img src="/images/logos/scylladb-logo.webp" alt="ScyllaDB" className="w-5 h-5" />
      case 'MSSQL':
      case 'Cassandra':
      case 'SQLite':
        return <ServerIcon className="w-5 h-5 text-gray-500" />
      case 'Milvus':
        return <img src="/images/logos/milvus-logo.svg" alt="Milvus" className="w-5 h-5" />
    }
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={sortableStyle}
        className={clsx(
          'flex items-center gap-3 px-4 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200',
          'hover:bg-gray-50 dark:hover:bg-gray-750 hover:shadow-sm',
          badgeState === 'active' && 'border-green-500 dark:border-green-400 ring-1 ring-green-500/20',
          badgeState === 'offline' && 'border-amber-500 dark:border-amber-400 ring-1 ring-amber-500/20',
          badgeState === 'error' && 'border-red-500 dark:border-red-400 ring-1 ring-red-500/20',
          isDragging && 'border-dashed border-blue-400/50 dark:border-blue-500/30',
          isHighlighted && 'ring-2 ring-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
        )}
      >
        {/* Drag Handle Indicator - listeners/attributes here so only the handle triggers drag */}
        <div
          {...attributes}
          {...listeners}
          className={clsx(
            'flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
            !disabled && 'cursor-grab active:cursor-grabbing'
          )}
        >
          <Bars2Icon className="w-5 h-5" />
        </div>

        {/* Database Type Icon */}
        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
          {getDatabaseTypeIcon()}
        </div>

        {/* Connection Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {connection.displayName}
            </h3>
            {connection.isLocked && (
              <LockClosedIcon className="w-4 h-4 text-amber-500 flex-shrink-0" title="Locked for safety" />
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="truncate">
              {connection.host}:{connection.port}
            </span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span className="inline-flex items-center gap-1">
              <span>{connection.databaseType}</span>
              {isRedisClusterConnection(connection) && (
                <span className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded">
                  <ServerStackIcon className="w-2.5 h-2.5" />
                  Cluster
                  {getClusterNodeCount(connection) > 0 && (
                    <span className="text-purple-500 dark:text-purple-300">
                      ({getClusterNodeCount(connection)})
                    </span>
                  )}
                </span>
              )}
            </span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span>{connection.username}</span>
            {connection.defaultDatabase && (
              <>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span className="truncate">{connection.defaultDatabase}</span>
              </>
            )}
          </div>
        </div>

        {/* Status Indicator */}
        {badgeState !== 'none' && (
          <div className={clsx('flex items-center gap-1 flex-shrink-0', getStatusColor())}>
            {getStatusIcon()}
            <span className="text-xs font-medium">{getStatusLabel()}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleConnectClick}
            disabled={testConnectionMutation.isPending || isCheckingAgent}
            className={clsx(
              'flex items-center gap-1 text-xs px-3 py-1.5 rounded-md transition-colors',
              badgeState === 'active'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : badgeState === 'offline' || badgeState === 'error'
                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            )}
          >
            {isCheckingAgent || testConnectionMutation.isPending ? (
              <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current" />
            ) : (
              <PlayIcon className="w-3.5 h-3.5" />
            )}
            <span>
              {badgeState === 'active'
                ? 'Active'
                : badgeState === 'offline' || badgeState === 'error'
                  ? 'Reconnect'
                  : isCheckingAgent
                    ? 'Checking...'
                    : testConnectionMutation.isPending
                      ? 'Connecting...'
                      : 'Connect'}
            </span>
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            <PencilIcon className="w-3.5 h-3.5" />
            <span>Edit</span>
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleteConnectionMutation.isPending}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 transition-colors"
          >
            <TrashIcon className="w-3.5 h-3.5" />
            <span>Delete</span>
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDelete}
        title="Delete Connection"
        message={`Are you sure you want to delete the connection "${connection.displayName}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isDangerous={true}
      />

      {connection.isLocked && (
        <SafetyLockDialog
          isOpen={showSafetyLock}
          onClose={handleSafetyLockClose}
          onConfirm={handleSafetyLockConfirm}
          datasourceName={connection.displayName || 'Unknown'}
        />
      )}
    </>
  )
})
